import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircleIcon, ExclamationCircleIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, CheckIcon } from '@heroicons/react/solid'

import Loading from '@/shared/Loading'
import useQuery from '@/hooks/useQuery'
import classNames from '@/utils/classNamesLocal'
import { useGlobalState } from '@/state'

import RoleIndex from '@/pages/ManageProjects/ProjectEdit/RoleIndex'
import RequiredInfoForm from '@/pages/ManageProjects/ProjectEdit/RequiredInformation'
import OptionalInfoForm from '@/pages/ManageProjects/ProjectEdit/OptionalInformation'
import ProjectImage from '@/pages/ManageProjects/ProjectEdit/ProjectImage'

const STEPS = [
  { key: 'basics', label: 'Basics', blurb: 'Name, deadline, pay' },
  { key: 'roles', label: 'Roles', blurb: 'Who you need' },
  { key: 'details', label: 'Details', blurb: 'Category, description, image' },
  { key: 'review', label: 'Publish', blurb: 'Check and go live' }
]

const Stepper = ({ current, furthest, onJump }) => (
  <nav aria-label='Progress'>
    <ol className='flex items-start'>
      { STEPS.map((step, i) => {
        const done = i < current
        const active = i === current
        const reachable = i <= furthest
        return <li key={step.key} className={classNames('relative flex-1', i < STEPS.length - 1 && 'pr-4 sm:pr-8')}>
          { i < STEPS.length - 1 && <div className='absolute left-4 right-0 top-4 h-0.5 -translate-y-1/2' aria-hidden='true'>
            <div className={classNames('h-full', done ? 'bg-cccblue' : 'bg-gray-200 dark:bg-gray-600')} />
          </div> }
          <button type='button' onClick={() => reachable && onJump(i)} disabled={!reachable} aria-current={active ? 'step' : undefined}
            className={classNames('group relative flex flex-col items-start text-left focus:outline-none', reachable ? 'cursor-pointer' : 'cursor-default')}>
            <span className={classNames('flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors',
              done ? 'border-cccblue bg-cccblue text-white' : active ? 'border-cccblue bg-white text-cccblue dark:bg-gray-900' : 'border-gray-300 bg-white text-gray-400 dark:border-gray-600 dark:bg-gray-800')}>
              { done ? <CheckIcon className='h-5 w-5' /> : i + 1 }
            </span>
            <span className={classNames('mt-2 text-xs font-semibold sm:text-sm', active || done ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400')}>{step.label}</span>
            <span className='hidden text-xs text-gray-500 dark:text-gray-400 sm:block'>{step.blurb}</span>
          </button>
        </li>
      })}
    </ol>
  </nav>
)

const CheckRow = ({ ok, required, label, detail, fix, onFix }) => (
  <li className='flex items-start gap-x-3 py-3'>
    { ok
      ? <CheckCircleIcon className='mt-0.5 h-5 w-5 flex-shrink-0 text-green-500' />
      : <ExclamationCircleIcon className={classNames('mt-0.5 h-5 w-5 flex-shrink-0', required ? 'text-cccorange' : 'text-gray-400')} /> }
    <div className='min-w-0 flex-1'>
      <div className='flex flex-wrap items-center gap-x-2'>
        <span className='text-sm font-semibold text-gray-900 dark:text-white'>{label}</span>
        { !ok && <span className={classNames('rounded-full px-2 py-0.5 text-[11px] font-semibold', required ? 'bg-cccorange/15 text-cccorange' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300')}>{ required ? 'needed' : 'nice to have' }</span> }
      </div>
      { detail && <div className='text-sm text-gray-600 dark:text-gray-300'>{detail}</div> }
    </div>
    { !ok && fix && <button type='button' onClick={onFix} className='flex-shrink-0 text-sm font-semibold text-cccblue hover:underline'>{fix}</button> }
  </li>
)

const Review = ({ project, roles, onJump, publish, publishing }) => {
  const [currentUser] = useGlobalState('currentUser')
  const named = !!project.name && project.name !== 'Untitled project'
  const deadlineOk = project.deadlineDate ? new Date(project.deadlineDate) >= new Date(new Date().toDateString()) : false
  const hasRoles = roles.length > 0
  const categorized = !!project.category
  const unsure = project.category === 'Unsure'
  const typed = !!project.classification
  const described = (project.description || '').replace(/<[^>]*>/g, '').trim().length >= 80
  const pictured = !!project.imageUrl
  const ready = named && deadlineOk && hasRoles && categorized && typed

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Ready to go live?</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Everything with a green check is good. Orange means I can't publish yet. Grey is stuff that gets you more auditions.</p>
      <p className='mt-3 text-sm text-gray-600 dark:text-gray-300'>Publishing lists the project in search, opens every role to auditions, and starts the clock on your deadline.</p>
    </div>
    <div className='md:col-span-3 rounded-xl border-t-2 border-cccorange bg-cccblue/5 shadow dark:border-cccpurple-alt dark:bg-gray-800'>
      <ul className='divide-y divide-gray-200 px-4 dark:divide-gray-700'>
        <CheckRow ok={named} required label='It has a name' detail={named ? project.name : 'Right now it\'s called "Untitled project". Bold choice.'} fix='Name it' onFix={() => onJump(0)} />
        <CheckRow ok={deadlineOk} required label='Deadline is in the future' detail={project.deadlineDate ? `Submissions close ${project.deadlineDate} at ${project.deadlineTime} ${project.deadlineAmpm}` : 'No deadline set.'} fix='Fix deadline' onFix={() => onJump(0)} />
        <CheckRow ok={hasRoles} required label={hasRoles ? `${roles.length} ${roles.length === 1 ? 'role' : 'roles'} to fill` : 'At least one role'} detail={hasRoles ? roles.map((r) => r.name).join(', ') : 'Nobody can audition for nothing.'} fix='Add a role' onFix={() => onJump(1)} />
        <CheckRow ok={categorized && !unsure} required={!categorized} label={unsure ? 'A specific category' : 'Category picked'}
          detail={unsure ? 'Marked "Unsure", which is allowed. It stays hidden from search until a moderator sorts it, so picking one yourself gets you seen sooner.' : categorized ? project.category : 'Talent filter by category. No category, no eyeballs.'}
          fix='Pick one' onFix={() => onJump(2)} />
        <CheckRow ok={typed} required label='Original or fan work' detail={typed ? project.classification : 'One click. I promise.'} fix='Pick one' onFix={() => onJump(2)} />
        <CheckRow ok={described} label='A real description' detail={described ? 'Looks meaty enough.' : 'Under a couple of sentences. People skip projects that don\'t explain themselves.'} fix='Write more' onFix={() => onJump(2)} />
        <CheckRow ok={pictured} label='A project image' detail={pictured ? 'Looking sharp.' : 'Projects with their own image get noticed. The default one is fine, but it\'s the default one.'} fix='Add one' onFix={() => onJump(2)} />
        { currentUser && !currentUser.hasBio && <CheckRow ok={false} label='Your profile says who you are'
          detail="Talent check out who they'd be working with before auditioning. Your bio is empty. A couple of sentences and a link go a long way."
          fix='Write a bio' onFix={() => window.open('/profile', '_blank')} /> }
      </ul>
      <div className='flex flex-col items-center gap-3 border-t border-gray-200 p-4 dark:border-gray-700 sm:flex-row sm:justify-between'>
        <span className='text-sm text-gray-600 dark:text-gray-300'>{ ready ? 'All the needed stuff is done. Hit it.' : 'Sort out the orange items and the big button lights up.' }</span>
        <button type='button' onClick={publish} disabled={!ready || publishing}
          className={classNames('inline-flex items-center gap-x-2 rounded-full px-6 py-2.5 text-sm font-bold text-white shadow focus:outline-none focus:ring-2 focus:ring-cccblue focus:ring-offset-2',
            ready && !publishing ? 'bg-gradient-to-r from-cccpurple via-cccblue to-cccorange hover:opacity-90' : 'cursor-not-allowed bg-gray-300 dark:bg-gray-600')}>
          <StarIcon className={classNames('h-4 w-4', ready && !publishing && 'animate-bounce')} />
          { publishing ? 'Publishing...' : 'Publish this thing' }
        </button>
      </div>
    </div>
  </div>
}

const ManageProjectStart = () => {
  const { id } = useParams()
  const [project, setProject] = useState(null)
  const [roles, setRoles] = useState([])
  const [step, setStep] = useState(0)
  const [furthest, setFurthest] = useState(0)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [, setToast] = useGlobalState('toast')
  const { getRequest, putpostRequest } = useQuery()
  const basicsRef = useRef()
  const detailsRef = useRef()

  const loadProject = (cb) => {
    getRequest(`/api/v3/manage/projects/${id}/edit`, {}, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setProject(jsonData.project)
      if (cb) { cb(jsonData.project) }
    })
  }
  const loadRoles = () => {
    getRequest(`/api/v3/manage/projects/${id}/roles`, {}, (err, jsonData) => {
      if (err) { return }
      setRoles(jsonData.roles)
    })
  }

  useEffect(() => { loadProject(); loadRoles() }, [id])

  const goTo = (i) => {
    setStep(i)
    setFurthest((f) => Math.max(f, i))
    window.scrollTo({ top: 0, behavior: 'smooth' })
    if (STEPS[i].key === 'review') { loadProject(); loadRoles() }
  }

  // "Save and continue" saves the form on screen before moving. A failed
  // save (blank name, server error) keeps you on the step with the errors.
  const next = async () => {
    const key = STEPS[step].key
    const ref = key === 'basics' ? basicsRef : key === 'details' ? detailsRef : null
    if (ref?.current) {
      setSaving(true)
      const ok = await ref.current.save()
      setSaving(false)
      if (!ok) {
        setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
          <p className='text-sm font-medium text-red-600 dark:text-red-100'>Not so fast</p>
          <p className='mt-1 text-sm text-red-400 dark:text-red-300'>Something on this step needs fixing before I can save it.</p>
        </div>)
        return
      }
    }
    goTo(step + 1)
  }

  const publish = () => {
    setPublishing(true)
    putpostRequest(`/api/v3/manage/projects/${project.id}/change_status`, 'POST', { status: 'open' }, (err, jsonData) => {
      setPublishing(false)
      if (err) {
        setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
          <p className='text-sm font-medium text-red-600 dark:text-red-100'>I couldn't publish that</p>
          <p className='mt-1 text-sm text-red-400 dark:text-red-300'>{ typeof err === 'string' ? err : (err.errors || 'Something on the server said no. Try once more?') }</p>
        </div>)
        return
      }
      if (jsonData.status === 'open') { window.location.href = `/manage/projects/${project.id}?published=true` }
    })
  }

  if (!project) { return <Loading /> }

  const current = STEPS[step].key
  const isLast = step === STEPS.length - 1

  return <div className={classNames('mt-4', !isLast && 'pb-24')}>
    <div className='rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-800 sm:p-6'>
      <Stepper current={step} furthest={furthest} onJump={goTo} />
    </div>

    <div className='mt-6'>
      {/* Forms stay mounted but hidden so typed-but-unsaved work survives a
          Back click. Only the review step re-fetches. */}
      <div hidden={current !== 'basics'}>
        <RequiredInfoForm ref={basicsRef} initialProject={project} embedded onSaved={setProject} />
      </div>
      <div hidden={current !== 'roles'}>
        <RoleIndex project={project} embedded onRolesChange={setRoles} />
      </div>
      <div hidden={current !== 'details'} className='space-y-8'>
        <OptionalInfoForm ref={detailsRef} initialProject={project} embedded onSaved={setProject} />
        <ProjectImage project={project} onChanged={(url) => setProject((p) => ({ ...p, imageUrl: url }))} />
      </div>
      { current === 'review' && <Review project={project} roles={roles} onJump={goTo} publish={publish} publishing={publishing} /> }
    </div>

    {/* Fixed to the viewport so it never moves as step content changes height.
        The sidebar is w-64 from lg up, so start the bar past it. */}
    { !isLast && <div className='fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur dark:border-gray-700 dark:bg-gray-800/95 lg:left-64'>
      <div className='container mx-auto flex items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8'>
        <button type='button' onClick={() => goTo(step - 1)} disabled={step === 0}
          className={classNames('inline-flex items-center gap-x-1 rounded-full px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-cccblue',
            step === 0 ? 'invisible' : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600')}>
          <ChevronLeftIcon className='h-4 w-4' /> Back
        </button>
        <div className='hidden text-sm text-gray-500 dark:text-gray-400 sm:block'>Step {step + 1} of {STEPS.length}: {STEPS[step].label}</div>
        <button type='button' onClick={next} disabled={saving}
          className='inline-flex items-center gap-x-1 rounded-full bg-cccblue px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cccblue-alt focus:outline-none focus:ring-2 focus:ring-cccblue focus:ring-offset-2 disabled:cursor-wait disabled:opacity-70'>
          { saving ? 'Saving...' : current === 'roles' ? 'Continue' : 'Save and continue' }
          <ChevronRightIcon className='h-4 w-4' />
        </button>
      </div>
    </div> }
  </div>
}

export default ManageProjectStart
